import React from 'react';
import { normalizeTextToWinSettings } from 'src/modules/campaigns/helpers';
import { TextToWinSettings } from 'src/modules/campaigns/types';
import { TextToWinSettingsForm } from './TextToWinSettingsForm';
import {
  validateTextToWinSettings,
  validateTextToWinSettingsOrder,
} from '../../helpers/textToWinSettingsHelpers';
import { useBuilderMetadata, useCampaignFormContext, useIsReadOnly } from '../../context';

import { useMessageTemplates } from '../../hooks';

/**
 * Prize-draw settings. Reuses the shared TextToWinSettingsForm so the layout,
 * copy and validation messages stay in one place.
 *
 * Error display: before the first submit only the date-ordering errors show;
 * afterwards the full required-field set does.
 */
export const TextToWinSettingsField: React.FC = () => {
  const { values, setField, isSubmitted } = useCampaignFormContext();
  const metadata = useBuilderMetadata() ?? undefined;
  const disabled = useIsReadOnly();

  // Same rule as the message editors: TemplateText freezes the template list it
  // mounts with, so the prize-draw copy fields wait for it too.
  const messageTemplates = useMessageTemplates();

  const settings = normalizeTextToWinSettings(values.textToWinSettings);
  const errors = isSubmitted
    ? validateTextToWinSettings(settings)
    : validateTextToWinSettingsOrder(settings);

  const handleSettingsChange = <K extends keyof TextToWinSettings>(
    name: K,
    value: TextToWinSettings[K],
  ) => {
    setField('textToWinSettings', { ...settings, [name]: value });
  };

  if (messageTemplates === null) return null;

  return (
    <TextToWinSettingsForm
      disabled={disabled}
      errors={errors}
      settings={settings}
      metadata={metadata}
      messageTemplates={messageTemplates}
      onSettingsChange={handleSettingsChange}
    />
  );
};
